// src/pages/AuthActionPage.js
//
// Pass 88.3 §4 — the BRANDED (Voletus) Firebase email-action handler. Replaces Firebase's default,
// disconnected reset screen. Handles mode=resetPassword (verify oobCode → new password → confirm → sign in →
// return into the app). NEVER a blank screen: invalid/expired codes, validation, busy and success states are
// all explicit. On successful reset it signs the user in with the new password, refreshes canonical
// AuthContext, and returns to a safe continue path (e.g. a pending invite) or the dashboard; if auto sign-in
// fails it redirects to /login?passwordReset=success with a visible banner.
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../AuthContext';
import { PRODUCT_NAME } from '../config/product';
import { AUTH_T as T } from '../components/auth/AuthShell';

// Only same-origin relative paths are honoured as a continue target (never an external/open redirect).
function safeContinue(raw) {
  const v = String(raw || '');
  if (v.startsWith('/') && !v.startsWith('//')) return v;
  return '';
}

const input = { width: '100%', boxSizing: 'border-box', padding: '11px 12px', fontSize: 14.5, fontFamily: T.f, border: `1px solid ${T.borderStrong}`, borderRadius: 10, background: '#fff', color: T.ink, outline: 'none' };
const label = { display: 'block', fontSize: 12.5, fontWeight: 600, color: T.ink2, margin: '0 0 6px' };
const primaryBtn = (disabled) => ({ width: '100%', padding: '12px 14px', fontSize: 15, fontWeight: 700, fontFamily: T.f, color: '#fff', background: disabled ? '#9DB4F0' : T.blue, border: 'none', borderRadius: 10, cursor: disabled ? 'default' : 'pointer', marginTop: 16 });

export default function AuthActionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserData } = useAuth() || {};
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const mode = params.get('mode') || '';
  const oobCode = params.get('oobCode') || '';
  const continueUrl = safeContinue(params.get('continueUrl'));

  const [phase, setPhase] = useState('verifying'); // verifying | ready | invalid | done
  const [accountEmail, setAccountEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (mode !== 'resetPassword' || !oobCode) { setPhase('invalid'); return undefined; }
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => { if (!cancelled) { setAccountEmail(email || ''); setPhase('ready'); } })
      .catch(() => { if (!cancelled) setPhase('invalid'); });
    return () => { cancelled = true; };
  }, [mode, oobCode]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (pw.length < 8) { setError('Choose a password of at least 8 characters.'); return; }
    if (pw !== pw2) { setError('Those passwords do not match.'); return; }
    setBusy(true);
    try {
      await confirmPasswordReset(auth, oobCode, pw);
      // Return into the app: sign in with the new password, refresh canonical AuthContext, go to a safe target.
      try {
        await signInWithEmailAndPassword(auth, accountEmail, pw);
        if (typeof refreshUserData === 'function') await refreshUserData();
        setPhase('done');
        navigate(continueUrl || '/dashboard', { replace: true });
      } catch (signInErr) {
        navigate('/login?passwordReset=success', { replace: true });
      }
    } catch (err) {
      setBusy(false);
      setError(err?.code === 'auth/expired-action-code' || err?.code === 'auth/invalid-action-code'
        ? 'This reset link has expired or already been used. Request a new one from the sign-in page.'
        : 'Could not update your password. Please try again.');
    }
  };

  const Card = ({ children }) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg || '#f4f4f2', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '28px 26px' }}>
        <div style={{ fontFamily: T.fD, fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px', color: T.ink, marginBottom: 18 }}>{PRODUCT_NAME}</div>
        {children}
      </div>
    </div>
  );

  if (phase === 'verifying') return <Card><p style={{ fontSize: 14, color: T.ink2 }}>Checking your reset link…</p></Card>;
  if (phase === 'invalid') return (
    <Card>
      <h2 style={{ fontFamily: T.fD, fontSize: 24, fontWeight: 800, margin: '0 0 8px' }}>Reset link problem</h2>
      <p data-testid="action-invalid" style={{ fontSize: 14, color: T.ink2, margin: '0 0 18px' }}>This password reset link is invalid, expired, or already used.</p>
      <button type="button" onClick={() => navigate('/login', { replace: true })} style={primaryBtn(false)}>Back to sign in</button>
    </Card>
  );

  return (
    <Card>
      <h2 style={{ fontFamily: T.fD, fontSize: 24, fontWeight: 800, margin: '0 0 6px' }}>Choose a new password</h2>
      <p style={{ fontSize: 14, color: T.ink2, margin: '0 0 18px' }}>for {accountEmail ? <strong>{accountEmail}</strong> : `your ${PRODUCT_NAME} account`}</p>
      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="new-pw" style={label}>New password</label>
        <input id="new-pw" data-testid="new-password" type={show ? 'text' : 'password'} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required style={{ ...input, marginBottom: 12 }} />
        <label htmlFor="new-pw2" style={label}>Confirm password</label>
        <input id="new-pw2" data-testid="confirm-password" type={show ? 'text' : 'password'} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required style={input} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.ink2, marginTop: 10 }}>
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show password
        </label>
        {error && <div data-testid="action-error" role="alert" style={{ color: '#B91C1C', fontSize: 13, marginTop: 10 }}>{error}</div>}
        <button type="submit" data-testid="action-submit" disabled={busy} style={primaryBtn(busy)}>{busy ? 'Updating password…' : 'Update password'}</button>
      </form>
    </Card>
  );
}
